import { useCallback, useEffect, useState } from 'react'
import Layout from 'containers/Layout'
import { Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom'

import apiGet from 'lib/network/apiGet'
import { forEach, get, isArray, isObject, trim, some } from 'lodash'
import Booking from './booking'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import BookingLogsDrawer from './bookingLogs'
import { useSelector } from 'react-redux'
import useModal from 'hooks/useModal'
import ChangeCarWizard from './modals/ChangeCarWizard'
import ExtendBookingModal from './modals/ExtendBookingModal'
import RejectBookingModal from './modals/RejectBookingModal'
import Stream from 'lib/network/apiStream'
import { useTranslation } from 'react-i18next'
import LoadingIcon from 'components/atoms/LoadingIcon'
import { isCompanyDashboardUser, isPartnerDashboardUser } from 'lib/auth/dashboardRole'
import { isExtendedBooking } from './bookingDetailHelpers'
import PolicyCancelModal from './modals/PolicyCancelModal'
import BookingRefundEngine from './BookingRefundEngine'
import { ArrowUturnLeftIcon } from '@heroiconsv2/react/24/outline'

const VIEW_BOOKING_TOOLBAR = 'flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2'
const BTN_SOLID_DARK =
  'w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs sm:text-sm font-semibold rounded-lg transition focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 text-white bg-blue-900 shadow-sm hover:bg-blue-800 focus:ring-blue-700'
const BTN_SOLID_RED =
  'w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs sm:text-sm font-semibold rounded-lg transition focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 text-white bg-red-600 shadow-sm hover:bg-red-700 focus:ring-red-500'
const BTN_GHOST =
  'w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs sm:text-sm font-semibold rounded-lg transition focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 text-slate-700 bg-white border border-slate-200 shadow-sm hover:bg-slate-50 focus:ring-slate-400'

const ViewBooking = () => {
  const history = useHistory()
  const location = useLocation()
  const { t } = useTranslation()

  const [isEdit, setIsEdit] = useState(null)
  const [bookingData, setBookingData] = useState({})
  /** True until the initial booking fetch (or refetch) completes — shows page loader. */
  const [loading, setLoading] = useState(true)
  /** Set when the booking cannot be loaded (e.g. cross-company 403). */
  const [accessError, setAccessError] = useState(null)
  const [acceptLoading, setAcceptLoading] = useState(false)
  const [rejectLoading, setRejectLoading] = useState(false)
  const [loadingCarId, setLoadingCarId] = useState(null)
  const [streamConnected, setStreamConnected] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  /** Set when Ops picks the refund-engine policy option; holds the notification choice for the policy cancel dialog. */
  const [policyCancel, setPolicyCancel] = useState(null)
  /** Bumps only when opening Change Car so SSE/bookingData updates do not remount the wizard (resets step). */
  const [changeCarWizardSessionKey, setChangeCarWizardSessionKey] = useState(0)
  const userFromStore = useSelector(state => get(state, 'user.user'))
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')
  // eslint-disable-next-line
  const isCompanyRole = isCompanyDashboardUser(userFromStore)
  const isPartnerOnly = isPartnerDashboardUser(userFromStore)

  const id = useParams()?.id

  const baseUrl = process.env.REACT_APP_BASE_URL
  const url = `${baseUrl}/admin/status/stream/${id}`

  const fetchBooking = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setAccessError(null)
    try {
      // Handle errors in this page so a 403/null payload does not crash Object.entries
      // and leave an empty booking summary on screen.
      const response = await apiGet(`booking/${id}`, null, false)

      if (!response?.success || !isObject(response.data)) {
        const isForbidden = response?.code === 403 || /access|permission/i.test(String(response?.message || ''))
        const message = isForbidden
          ? t('booking.messages.noPermission', { defaultValue: "You don't have permission to view this booking." })
          : response?.message || t('booking.messages.loadFailed', { defaultValue: 'Failed to load booking' })
        setAccessError(message)
        setBookingData({})
        notify.error(message)
        return
      }

      forEach(Object.entries(response.data), ([key, value]) => {
        if (value === 'null') {
          delete response.data[key]
        }
      })

      setBookingData(response.data)
    } catch (error) {
      const message = error?.message || t('booking.messages.loadFailed', { defaultValue: 'Failed to load booking' })
      setAccessError(message)
      notify.error(message)
      setBookingData({})
    } finally {
      setLoading(false)
    }
  }, [id, t])

  useEffect(() => {
    if (id) {
      setIsEdit(true)
      fetchBooking()
    } else {
      setLoading(false)
    }
  }, [id, fetchBooking])

  const handleStatusChange = newStatus => {
    if (newStatus !== bookingData.status) {
      setBookingData(prevData => ({
        ...prevData,
        status: newStatus,
      }))
      notify.info(`Booking status updated to: ${newStatus}`)
    }
  }

  const handleConnectionChange = connected => {
    setStreamConnected(connected)
  }

  const getObjectId = value => {
    if (!value) return ''
    if (isObject(value)) return trim(String(value._id || value.id || ''))
    return trim(String(value))
  }

  const getDiscountCouponContext = booking => {
    const discount = booking?.discount
    if (!discount || !isObject(discount)) {
      return { hasDiscount: false, isValidForCompany: false, couponCode: '' }
    }
    const couponCode = trim(String(discount.cupon ?? ''))
    const bookingCompanyId = getObjectId(booking?.rentalCar?.company)
    const discountCompaniesRaw = discount.company
    const discountCompanies = isArray(discountCompaniesRaw) ? discountCompaniesRaw : discountCompaniesRaw ? [discountCompaniesRaw] : []
    const isValidForCompany = bookingCompanyId !== '' && some(discountCompanies, company => getObjectId(company) === bookingCompanyId)
    return { hasDiscount: true, isValidForCompany, couponCode }
  }

  // Paymob: a cancelled, partly refunded booking can be refunded again up to the remaining amount.
  // Paymob refund figures computed by the backend from the database (the cancel endpoint re-checks them):
  // max Paymob refund = min(current booking total, Paymob remaining, original - wallet returned - Paymob refunded).
  const serverRefundSummary = bookingData?.paymobRefundSummary
  const omr = cents => Number(cents || 0) / 1000
  const paymobRefundSummary = serverRefundSummary
    ? {
        originalBookingAmount: omr(serverRefundSummary.original_booking_cents),
        walletReturned: omr(serverRefundSummary.wallet_returned_cents),
        paymobRefunded: omr(serverRefundSummary.paymob_refunded_cents),
        remainingToReturn: omr(serverRefundSummary.remaining_to_return_cents),
        maximumRefundable: omr(serverRefundSummary.max_paymob_refund_cents),
      }
    : null
  const paymobMaxRefundableOmr = paymobRefundSummary ? paymobRefundSummary.maximumRefundable : 0
  const canRefundPaymobAgain = bookingData?.status === 'cancelled' && bookingData?.paymentStatus === 'partialrefund' && paymobMaxRefundableOmr > 0

  const handlePaymobRefundAgain = () => {
    notify.deleteCancelConfirm({
      title: 'Refund Paymob payment',
      message: 'Refund more of the Paymob payment for this cancelled booking.',
      dangerText: 'Refund',
      showRefundOptions: true,
      showNotificationOptions: false,
      isPaymob: true,
      refundOnly: true,
      maxRefundAmount: paymobMaxRefundableOmr,
      refundSummary: paymobRefundSummary,
      bookingTotal: Number(bookingData?.totalPrice || 0),
      onConfirm: async refundMethod => {
        const payload = { cancelPaymentType: 'refund' }
        if (refundMethod?.refundAmount !== undefined) payload.refundAmount = refundMethod.refundAmount
        const response = await apiPost(`booking/cancel/${id}`, payload)
        if (response?.success) {
          notify.success('Refund processed successfully')
          fetchBooking()
        }
      },
    })
  }

  const handleCancel = async () => {
    notify.deleteCancelConfirm({
      title: 'Cancel Booking',
      message: 'Are you sure you want to cancel this booking?',
      showRefundOptions: true,
      showNotificationOptions: true,
      isPaymob: bookingData?.paymentSessionMetadata?.gateway === 'paymob',
      walletOnly: bookingData?.paymentSessionMetadata?.payment_method === 'Wallet',
      walletShare: Number(bookingData?.paymentSessionMetadata?.metadata?.walletAmountToUse || 0),
      maxRefundAmount: paymobRefundSummary ? paymobMaxRefundableOmr : undefined,
      refundSummary: paymobRefundSummary,
      bookingTotal: Number(bookingData?.totalPrice || 0),
      showPolicyOption: true,
      onConfirm: async (refundMethod, sendCancellationNotification = true) => {
        if (refundMethod === 'policy') {
          setPolicyCancel({ sendCancellationNotification: Boolean(sendCancellationNotification) })
          return
        }
        try {
          const discountCouponCtx = getDiscountCouponContext(bookingData)
          const payload = {
            sendCancellationNotification: Boolean(sendCancellationNotification),
          }
          // eslint-disable-next-line
          const isRefundWithRrn = refundMethod && typeof refundMethod === 'object'
          if (isRefundWithRrn) {
            payload.cancelPaymentType = refundMethod.method
            if (refundMethod.rrn) payload.rrn = refundMethod.rrn
            if (refundMethod.refundAmount !== undefined) payload.refundAmount = refundMethod.refundAmount
          } else if (refundMethod) {
            payload.cancelPaymentType = refundMethod
          }
          if (discountCouponCtx.hasDiscount && !discountCouponCtx.isValidForCompany) {
            notify.error(t('updateBooking.changeCarWizard.step3.oldBookingCouponInvalidForCar'))
          }
          if (discountCouponCtx.hasDiscount && discountCouponCtx.isValidForCompany && discountCouponCtx.couponCode !== '') {
            payload.couponCode = discountCouponCtx.couponCode
          }

          const processCancelSuccess = () => {
            notify.success('Booking cancelled successfully')
            history.push('/bookings')
          }

          // showError=false so insufficient-wallet can open a confirm alert instead of only an error toast
          const response = await apiPost(`booking/cancel/${id}`, payload, 0, false)

          if (response?.success) {
            processCancelSuccess()
            return
          }

          if (response?.errorCode === 'INSUFFICIENT_WALLET_FOR_REFUND') {
            notify.alertConfirm({
              title: 'Insufficient wallet balance',
              message: response?.message || 'Wallet balance is empty or less than the amount previously added for this booking. Cancel this booking anyway?',
              dangerText: 'Cancel booking anyway',
              onConfirm: async () => {
                try {
                  const forcedResponse = await apiPost(`booking/cancel/${id}`, { ...payload, forceCancel: true }, 0, true)
                  if (forcedResponse?.success) {
                    processCancelSuccess()
                  }
                } catch (error) {
                  notify.error(error?.message || 'Failed to process booking')
                }
              },
            })
            return
          }

          if (response?.message) {
            notify.error(response.message, null, response.err)
          }
        } catch (error) {
          notify.error(error?.message || 'Failed to process booking')
        } finally {
          setLoadingCarId(false)
        }
      },
    })
  }

  const handleUpdateBooking = async (status, extra = {}) => {
    try {
      if (status === 'confirmed') {
        setAcceptLoading(true)
      } else if (status === 'rejected') {
        setRejectLoading(true)
      }

      const response = await apiPost(`booking/${id}`, { status, ...extra })

      if (response?.success) {
        notify.success(`Booking ${status} successfully`)
      }
    } catch (error) {
      notify.error('Failed to update booking')
      fetchBooking()
    } finally {
      setAcceptLoading(false)
      setRejectLoading(false)
    }
  }

  const closeDrawer = () => {
    setIsDrawerOpen(false)
  }

  const [AddUserModal, openAddUserModel, closeModal] = useModal({
    content: (
      <ChangeCarWizard
        key={changeCarWizardSessionKey}
        bookingData={bookingData}
        bookingId={id}
        loadingCarId={loadingCarId}
        onSelectCar={() => {}}
        onCancelSuccess={() => {
          closeModal()
          notify.success(t('updateBooking.messages.bookingCancelledAndNewCreated') || 'Booking cancelled and new booking created successfully')
          history.push('/bookings')
        }}
        isAdmin={hasAdminAccess}
      />
    ),
    title: t('updateBooking.labels.changeCar') || 'Change Car',
    dimensions: {
      height: '650px',
      width: '950px',
    },
  })

  const [ExtendBookingModalShell, openExtendBookingModal] = useModal({
    content: (
      <ExtendBookingModal
        bookingData={bookingData}
        bookingId={id}
        onSuccess={data => {
          if (data?.id) {
            history.push(`/booking/${data.id}`)
          } else {
            fetchBooking()
          }
        }}
      />
    ),
    title: 'Extend Booking',
    dimensions: {
      height: '320px',
      width: '540px',
    },
  })

  const [RejectBookingModalShell, openRejectModal, closeRejectModal] = useModal({
    content: (
      <RejectBookingModal
        bookingData={bookingData}
        loading={rejectLoading}
        onCancel={() => closeRejectModal()}
        onConfirm={async payload => {
          closeRejectModal()
          await handleUpdateBooking('rejected', payload)
        }}
      />
    ),
    title: 'Reject Booking',
    closeable: true,
    customClass: 'overflow-visible',
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '520px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const canExtendBooking = bookingData?.status === 'confirmed' && bookingData?.paymentStatus === 'paid'

  useEffect(() => {
    const shouldAutoOpen = new URLSearchParams(location.search).get('extend') === 'true'
    if (shouldAutoOpen && canExtendBooking && !loading) {
      openExtendBookingModal()
      history.replace(`/booking/${id}`)
    }
  }, [location.search, canExtendBooking, loading, openExtendBookingModal, history, id])

  return (
    <Layout
      headerRight={
        <>
          <div className={VIEW_BOOKING_TOOLBAR}>
            {/* Cancel Booking Button - Admin only */}
            {!accessError && !isPartnerOnly && hasAdminAccess && !isCompanyRole && (bookingData?.status === 'pending' || bookingData?.status === 'confirmed') && (
              <div className="w-full sm:w-auto">
                <button onClick={() => handleCancel()} type="button" className={BTN_SOLID_RED} disabled={loading}>
                  Cancel Booking
                </button>
              </div>
            )}

            {/* After pickup, Ops can only cancel through the refund engine (company-fault cases) */}
            {!accessError && !isPartnerOnly && hasAdminAccess && !isCompanyRole && bookingData?.status === 'inProgress' && (
              <div className="w-full sm:w-auto">
                <button onClick={() => setPolicyCancel({ sendCancellationNotification: true })} type="button" className={BTN_SOLID_RED} disabled={loading}>
                  {t('refundEngine.booking.policyCancelTitle')}
                </button>
              </div>
            )}

            {/* Further Paymob refund - cancelled, partly refunded bookings (admin only) */}
            {!accessError && !isPartnerOnly && hasAdminAccess && !isCompanyRole && canRefundPaymobAgain && (
              <div className="w-full sm:w-auto">
                <button onClick={handlePaymobRefundAgain} type="button" className={BTN_SOLID_RED} disabled={loading}>
                  Refund (up to {paymobMaxRefundableOmr.toFixed(3)} OMR)
                </button>
              </div>
            )}

            {/* Accept/Reject Buttons - Pending status */}
            {!accessError && !isPartnerOnly && bookingData?.status === 'pending' && (
              <>
                <div className="w-full sm:w-auto">
                  <button onClick={() => openRejectModal()} type="button" className={BTN_SOLID_RED} disabled={loading || rejectLoading || acceptLoading}>
                    {rejectLoading ? 'Rejecting...' : 'Reject Booking'}
                  </button>
                </div>
                <div className="w-full sm:w-auto">
                  <button onClick={() => handleUpdateBooking('confirmed')} type="button" className={BTN_SOLID_DARK} disabled={loading || acceptLoading || rejectLoading}>
                    {acceptLoading ? 'Accepting...' : 'Accept Booking'}
                  </button>
                </div>
              </>
            )}

            {/* Change Car is not available from View Booking — use the Edit Booking screen. */}

            {!accessError && !isPartnerOnly && canExtendBooking && !isCompanyRole && (
              <div className="w-full sm:w-auto">
                <button type="button" className={BTN_SOLID_DARK} disabled={loading} onClick={() => openExtendBookingModal()}>
                  Extend Booking
                </button>
              </div>
            )}

            {/* View Logs Button */}
            {!accessError && !isPartnerOnly && (
              <div className="w-full sm:w-auto">
                <button onClick={() => setIsDrawerOpen(true)} type="button" className={BTN_GHOST} disabled={loading}>
                  {t('transactions.buttons.viewLogs')}
                </button>
              </div>
            )}

            {/* Go Back Button */}
            <div className="w-full sm:w-auto">
              <button
                onClick={() => {
                  history.push('/bookings')
                }}
                type="button"
                className={BTN_GHOST}
              >
                <ArrowUturnLeftIcon className="h-4 w-4 rtl:-scale-x-100" />
                {t('addEditPartner.buttons.goBack')}
              </button>
            </div>
          </div>
        </>
      }
      title={t('booking.sections.orderSummary')}
    >
      {!loading && !accessError && id ? (
        <div className="border-b border-slate-200/70 bg-white/95 px-3 sm:px-5">
          <Stream id={id} onStatusChange={handleStatusChange} url={url} onConnectionChange={handleConnectionChange} />
        </div>
      ) : null}

      {!loading && !accessError && id && hasAdminAccess && !isCompanyRole && !isPartnerOnly ? <BookingRefundEngine bookingId={id} /> : null}

      {/* Responsive container for the booking content */}
      <div className="relative flex min-h-[50vh] w-full flex-1 overflow-auto bg-slate-50/40">
        {loading ? (
          <div className="flex min-h-[min(70vh,560px)] w-full flex-col items-center justify-center gap-4 px-4 py-16" aria-busy="true" aria-live="polite">
            <LoadingIcon />
            <p className="text-sm font-medium text-slate-600">{t('common.loading')}</p>
          </div>
        ) : accessError ? (
          <div className="flex min-h-[min(70vh,560px)] w-full flex-col items-center justify-center gap-4 px-4 py-16 text-center" role="alert">
            <p className="max-w-md text-base font-semibold text-slate-800">{accessError}</p>
            <button type="button" className={BTN_GHOST} onClick={() => history.push('/bookings')}>
              {t('addEditPartner.buttons.goBack')}
            </button>
          </div>
        ) : (
          <Switch>
            <Route
              key={id}
              path="/booking/:id"
              component={() => <Booking refetch={fetchBooking} bookingData={bookingData} loading={loading} id={id} isEdit={isEdit} streamConnected={streamConnected} />}
            />
          </Switch>
        )}
      </div>

      <BookingLogsDrawer isDrawerOpen={isDrawerOpen} closeDrawer={closeDrawer} selectedBooking={bookingData} />
      <AddUserModal />
      <ExtendBookingModalShell />
      {policyCancel && (
        <PolicyCancelModal
          bookingId={id}
          sendCancellationNotification={policyCancel.sendCancellationNotification}
          onClose={() => setPolicyCancel(null)}
          onCancelled={() => history.push('/bookings')}
        />
      )}
      <RejectBookingModalShell />
    </Layout>
  )
}

export default ViewBooking
